import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import './MatchRoom.css';
import { useMatchRoomAnalytics } from './hooks/useMatchRoomAnalytics';
import { getStatusLabel, useMatchRooms } from './hooks/useMatchRooms';
import {
  DATASET_FILTERS,
  MODE_DEFINITIONS,
  formatPercent,
  formatRecord,
} from './utils/matchRoomAnalytics';

const MATCH_ROOM_TABS = [
  'Overview',
  'Map Pool',
  'Opponent',
  'Players',
  'Preparation',
  'Strategies',
  'Notes',
];

const SERIES_FORMATS = ['BO3', 'BO5', 'BO7'];

const formatDate = (value) => {
  if (!value) return 'Date TBD';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const getProgress = (tasks = []) => {
  const total = tasks.length;
  const complete = tasks.filter((task) => task.completed).length;
  return {
    complete,
    total,
    percent: total ? Math.round((complete / total) * 100) : 0,
  };
};

const makeLocalId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const formatDelta = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'No data';
  const rounded = Math.round(value);
  return `${rounded > 0 ? '+' : ''}${rounded} pp`;
};

const getDeltaClass = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  if (value >= 5) return 'is-positive';
  if (value <= -5) return 'is-negative';
  return 'is-neutral';
};

const DatasetFilter = ({ value, onChange }) => (
  <div className="match-room-dataset-filter" aria-label="Analytics dataset filter">
    {DATASET_FILTERS.map((option) => (
      <button
        type="button"
        key={option.value}
        className={value === option.value ? 'active' : ''}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const ResultSequence = ({ form }) => (
  <div className="match-room-form-sequence">
    {form.sequence.length ? form.sequence.map((item, index) => (
      <span
        key={`${item.mode}-${item.map}-${index}`}
        className={item.result === 'W' ? 'is-win' : 'is-loss'}
        title={`${item.mode} ${item.map}`}
      >
        {item.result}
      </span>
    )) : <em>No recent data</em>}
  </div>
);

const StatRecord = ({ label, record, recent }) => (
  <div className="match-room-stat-record">
    <span>{label}</span>
    <strong>{formatRecord(record)}</strong>
    <small>
      {record.played ? `${record.wins} wins / ${record.losses} losses / ${formatPercent(record.winRate)} WR` : 'No recorded data'}
    </small>
    <small>{record.played ? `${record.played} maps played` : '0 maps played'}</small>
    <small>{recent?.label || 'No recent data'}</small>
  </div>
);

const AnalyticsState = ({ loading, error, opponentName }) => {
  if (loading) {
    return <EmptyPanel title="Loading Matchup Analytics" body="Reading recorded team stats and opponent scouting for this season." />;
  }
  if (error) {
    return <EmptyPanel title="Unable to load analytics" body={error} />;
  }
  if (!opponentName) {
    return <EmptyPanel title="Opponent unavailable" body="Select an opponent for this Match Room before analytics can be calculated." />;
  }
  return null;
};

const EmptyPanel = ({ title, body, action }) => (
  <div className="match-room-empty-panel">
    <h3>{title}</h3>
    <p>{body}</p>
    {action}
  </div>
);

const MatchRoomCard = ({ room, teamName, onOpen }) => {
  const progress = getProgress(room.preparationTasks);

  return (
    <article className="match-room-card">
      <div className="match-room-card-top">
        <div>
          <h3>{teamName || 'Team'} vs {room.opponentName}</h3>
          <p>{room.competitionName || 'Competition TBD'} / {formatDate(room.matchDate)} / {room.format}</p>
        </div>
        <span className={`match-room-status status-${room.status}`}>{getStatusLabel(room.status)}</span>
      </div>

      <div className="match-room-progress-row">
        <span>Preparation</span>
        <strong>{progress.complete} / {progress.total} complete</strong>
      </div>
      <div className="match-room-progress-track" aria-hidden="true">
        <span style={{ width: `${progress.percent}%` }} />
      </div>

      <button type="button" className="match-room-card-action" onClick={() => onOpen(room.id)}>
        Open Match Room
      </button>
    </article>
  );
};

const MatchRoomFormModal = ({
  title,
  submitLabel,
  initialRoom,
  opponentOptions,
  competitionOptions,
  statusOptions,
  configLoading,
  configError,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState({
    opponentId: initialRoom?.opponentId || '',
    competitionId: initialRoom?.competitionId || '',
    matchDate: initialRoom?.matchDate || '',
    format: initialRoom?.format || 'BO5',
    status: initialRoom?.status || 'preparing',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!form.opponentId && opponentOptions.length === 1) {
      setForm((current) => ({ ...current, opponentId: opponentOptions[0].id }));
    }
    if (!form.competitionId && competitionOptions.length === 1) {
      setForm((current) => ({ ...current, competitionId: competitionOptions[0].id }));
    }
  }, [competitionOptions, form.competitionId, form.opponentId, opponentOptions]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const canSubmit =
    form.opponentId &&
    form.competitionId &&
    form.matchDate &&
    form.format &&
    opponentOptions.length > 0 &&
    competitionOptions.length > 0 &&
    !submitting;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      await onSubmit(form);
    } catch (error) {
      console.error('Failed to save Match Room', error);
      toast.error(error?.message || 'Failed to save Match Room.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="match-room-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div className="match-room-modal" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
        <div className="match-room-modal-header">
          <div>
            <span>Match Room</span>
            <h2>{title}</h2>
          </div>
          <button type="button" className="match-room-icon-btn" onClick={onClose} aria-label={`Close ${title}`}>
            x
          </button>
        </div>

        <form className="match-room-form" onSubmit={handleSubmit}>
          {configLoading && <p className="match-room-muted">Loading configured opponents and competitions...</p>}
          {configError && <p className="match-room-error">{configError}</p>}

          <label>
            <span>Opponent</span>
            <select
              value={form.opponentId}
              onChange={(event) => updateField('opponentId', event.target.value)}
              disabled={!opponentOptions.length}
            >
              <option value="">{opponentOptions.length ? 'Select opponent' : 'No opponents configured yet.'}</option>
              {opponentOptions.map((opponent) => (
                <option key={opponent.id} value={opponent.id}>
                  {opponent.region ? `${opponent.name} (${opponent.region})` : opponent.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Competition</span>
            <select
              value={form.competitionId}
              onChange={(event) => updateField('competitionId', event.target.value)}
              disabled={!competitionOptions.length}
            >
              <option value="">{competitionOptions.length ? 'Select competition' : 'No competitions configured yet.'}</option>
              {competitionOptions.map((competition) => (
                <option key={competition.id} value={competition.id}>
                  {competition.region ? `${competition.name} (${competition.region})` : competition.name}
                </option>
              ))}
            </select>
          </label>

          <div className="match-room-form-grid">
            <label>
              <span>Match Date</span>
              <input
                type="date"
                value={form.matchDate}
                onChange={(event) => updateField('matchDate', event.target.value)}
              />
            </label>

            <label>
              <span>Series Format</span>
              <select value={form.format} onChange={(event) => updateField('format', event.target.value)}>
                {SERIES_FORMATS.map((format) => (
                  <option key={format} value={format}>{format}</option>
                ))}
              </select>
            </label>
          </div>

          {initialRoom && (
            <label>
              <span>Status</span>
              <select value={form.status} onChange={(event) => updateField('status', event.target.value)}>
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </label>
          )}

          <div className="match-room-modal-actions">
            <button type="button" className="match-room-secondary-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="match-room-primary-btn" disabled={!canSubmit}>
              {submitting ? 'Saving...' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InsightList = ({ items, empty, teamName, opponentName, type }) => (
  <div className="match-room-insight-list">
    {items.length ? items.map((item) => (
      <div className="match-room-insight-item" key={`${item.mode}-${item.map}-${type}`}>
        <strong>{item.map} {item.mode}</strong>
        <span>
          {teamName || 'Our Team'}: {formatRecord(item.our)} / {formatPercent(item.our.winRate)}
        </span>
        <span>
          {opponentName}: {formatRecord(item.opponent)} / {formatPercent(item.opponent.winRate)}
        </span>
        <small>{formatDelta(item.delta)} / {item.confidence.label} confidence</small>
      </div>
    )) : <p className="match-room-muted">{empty}</p>}
  </div>
);

const OverviewTab = ({
  room,
  analytics,
  analyticsLoading,
  analyticsError,
  datasetFilter,
  setDatasetFilter,
  teamName,
  opponentName,
  onAddWinCondition,
  onRemoveWinCondition,
}) => {
  const [draftWinCondition, setDraftWinCondition] = useState('');
  const progress = getProgress(room.preparationTasks);

  const addWinCondition = async () => {
    const value = draftWinCondition.trim();
    if (!value) return;
    await onAddWinCondition(value);
    setDraftWinCondition('');
  };

  return (
    <div className="match-room-overview-grid">
      <section className="match-room-panel match-room-series-panel">
        <div className="match-room-panel-head match-room-analytics-head">
          <div>
            <span>Recorded Mode Comparison</span>
            <p>Statistical edge only. This compares submitted results, not veto preference.</p>
          </div>
          <DatasetFilter value={datasetFilter} onChange={setDatasetFilter} />
        </div>
        <AnalyticsState loading={analyticsLoading} error={analyticsError} opponentName={opponentName} />
        <div className="match-room-mode-grid">
          {analytics.modes.map((mode) => (
            <div className="match-room-mode-card match-room-mode-analytics-card" key={mode.id}>
              <div className="match-room-mode-card-head">
                <strong>{mode.label}</strong>
                <span className={`match-room-edge ${getDeltaClass(mode.delta)}`}>{mode.matchupLabel}</span>
              </div>
              <div className="match-room-mode-record-grid">
                <StatRecord label={teamName || 'Our Team'} record={mode.our} recent={mode.ourRecent} />
                <StatRecord label={opponentName || 'Opponent'} record={mode.opponent} recent={mode.opponentRecent} />
              </div>
              <p className="match-room-delta-copy">
                Delta: {formatDelta(mode.delta)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="match-room-panel">
        <div className="match-room-panel-head">
          <span>Our Advantages</span>
          <p>Eligible maps where both sides have enough recorded sample.</p>
        </div>
        <InsightList
          items={analytics.advantages}
          empty="No reliable map advantage is visible in the selected dataset yet."
          teamName={teamName}
          opponentName={opponentName || 'Opponent'}
          type="advantage"
        />
      </section>

      <section className="match-room-panel">
        <div className="match-room-panel-head">
          <span>Biggest Threats</span>
          <p>Eligible maps where the opponent has the statistical edge.</p>
        </div>
        <InsightList
          items={analytics.threats}
          empty="No reliable opponent map threat is visible in the selected dataset yet."
          teamName={teamName}
          opponentName={opponentName || 'Opponent'}
          type="threat"
        />
      </section>

      <section className="match-room-panel">
        <div className="match-room-panel-head">
          <span>Preparation Progress</span>
          <p>{progress.complete} / {progress.total} complete</p>
        </div>
        <div className="match-room-progress-large">{progress.percent}%</div>
        <div className="match-room-progress-track" aria-hidden="true">
          <span style={{ width: `${progress.percent}%` }} />
        </div>
      </section>

      <section className="match-room-panel match-room-win-panel">
        <div className="match-room-panel-head">
          <span>Win Conditions</span>
          <p>Saved with this Match Room.</p>
        </div>
        <div className="match-room-inline-form">
          <input
            value={draftWinCondition}
            onChange={(event) => setDraftWinCondition(event.target.value)}
            placeholder="Start fast on our strongest recorded S&D maps"
          />
          <button type="button" onClick={addWinCondition}>+ Add</button>
        </div>
        {room.winConditions.length > 0 ? (
          <ul className="match-room-list">
            {room.winConditions.map((item) => {
              const condition = typeof item === 'string' ? { id: item, text: item } : item;
              return (
                <li key={condition.id || condition.text}>
                  <span>{condition.text}</span>
                  <button type="button" onClick={() => onRemoveWinCondition(condition.id || condition.text)}>Remove</button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="match-room-muted">Add a short win condition to capture the early game plan.</p>
        )}
      </section>
    </div>
  );
};

const PreparationTab = ({ room, onToggleTask, onAddTask, onDeleteTask }) => {
  const [draftTask, setDraftTask] = useState('');

  const addTask = async () => {
    const value = draftTask.trim();
    if (!value) return;
    await onAddTask(value);
    setDraftTask('');
  };

  return (
    <section className="match-room-panel">
      <div className="match-room-panel-head">
        <span>Preparation</span>
        <p>Checklist progress updates this room everywhere in the Match Room UI.</p>
      </div>

      {room.preparationTasks.length > 0 ? (
        <div className="match-room-task-list">
          {room.preparationTasks.map((task) => (
            <label className="match-room-task" key={task.id}>
              <input
                type="checkbox"
                checked={Boolean(task.completed)}
                onChange={() => onToggleTask(task.id)}
              />
              <span>{task.title}</span>
              <button type="button" onClick={(event) => {
                event.preventDefault();
                onDeleteTask(task.id);
              }}>
                Remove
              </button>
            </label>
          ))}
        </div>
      ) : (
        <p className="match-room-muted">No preparation tasks yet.</p>
      )}

      <div className="match-room-inline-form">
        <input
          value={draftTask}
          onChange={(event) => setDraftTask(event.target.value)}
          placeholder="Add preparation task"
        />
        <button type="button" onClick={addTask}>+ Add Task</button>
      </div>
    </section>
  );
};

const NotesTab = ({ room, onSaveNotes }) => {
  const [draftNotes, setDraftNotes] = useState(room.notes || '');
  const [saveState, setSaveState] = useState('');

  useEffect(() => {
    setDraftNotes(room.notes || '');
    setSaveState('');
  }, [room.id, room.notes]);

  const saveNotes = async () => {
    setSaveState('saving');
    try {
      await onSaveNotes(draftNotes);
      setSaveState('saved');
      setTimeout(() => setSaveState(''), 1600);
    } catch (error) {
      console.error('Failed to save Match Room notes', error);
      setSaveState('');
      toast.error('Failed to save notes.');
    }
  };

  return (
    <section className="match-room-panel">
      <div className="match-room-panel-head match-room-notes-head">
        <div>
          <span>Notes</span>
          <p>Scouting notes and coach reminders saved with this Match Room.</p>
        </div>
        <button type="button" className="match-room-secondary-btn" onClick={saveNotes} disabled={saveState === 'saving'}>
          {saveState === 'saving' ? 'Saving...' : saveState === 'saved' ? 'Saved' : 'Save Notes'}
        </button>
      </div>
      <textarea
        className="match-room-notes"
        value={draftNotes}
        onChange={(event) => setDraftNotes(event.target.value)}
        placeholder="Add scouting notes, preparation ideas, matchup reminders, or coach instructions..."
      />
    </section>
  );
};

const MapSummaryCard = ({ label, item, side = 'our' }) => (
  <div className="match-room-map-summary-card">
    <span>{label}</span>
    {item ? (
      <>
        <strong>{item.map}</strong>
        <small>{formatRecord(item[side])} / {formatPercent(item[side].winRate)}</small>
        <small>{item[side].played} maps / {item[side].confidence.label} confidence</small>
      </>
    ) : (
      <p>No data</p>
    )}
  </div>
);

const MapPoolTab = ({
  analytics,
  analyticsLoading,
  analyticsError,
  datasetFilter,
  setDatasetFilter,
  teamName,
  opponentName,
}) => {
  const [selectedMode, setSelectedMode] = useState('HP');
  const [expandedMap, setExpandedMap] = useState('');
  const mode = analytics.modes.find((item) => item.id === selectedMode) || analytics.modes[0];

  useEffect(() => {
    setExpandedMap('');
  }, [selectedMode, datasetFilter]);

  return (
    <section className="match-room-panel match-room-map-pool">
      <div className="match-room-panel-head match-room-analytics-head">
        <div>
          <span>Map Pool</span>
          <p>Recorded map performance and usage. This does not represent picks, bans, or preference.</p>
        </div>
        <DatasetFilter value={datasetFilter} onChange={setDatasetFilter} />
      </div>

      <AnalyticsState loading={analyticsLoading} error={analyticsError} opponentName={opponentName} />

      <div className="match-room-mode-tabs">
        {MODE_DEFINITIONS.map((option) => (
          <button
            type="button"
            key={option.id}
            className={selectedMode === option.id ? 'active' : ''}
            onClick={() => setSelectedMode(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {mode && (
        <>
          <div className="match-room-map-summary-grid">
            <MapSummaryCard label="Our Strongest Recorded Map" item={mode.ourStrongestMap} side="our" />
            <MapSummaryCard label="Opponent Strongest Recorded Map" item={mode.opponentStrongestMap} side="opponent" />
            <MapSummaryCard label="Our Most Played" item={mode.ourMostPlayedMaps[0]} side="our" />
            <MapSummaryCard label="Opponent Most Played" item={mode.opponentMostPlayedMaps[0]} side="opponent" />
          </div>

          <div className="match-room-table-wrap">
            <table className="match-room-map-table">
              <thead>
                <tr>
                  <th>Map</th>
                  <th>{teamName || 'Our Team'}</th>
                  <th>WR</th>
                  <th>Usage</th>
                  <th>{opponentName || 'Opponent'}</th>
                  <th>WR</th>
                  <th>Usage</th>
                  <th>Delta</th>
                  <th>Confidence</th>
                  <th>Matchup</th>
                </tr>
              </thead>
              <tbody>
                {mode.maps.map((map) => {
                  const isExpanded = expandedMap === map.map;
                  const hasAnyData = map.our.played || map.opponent.played;
                  return (
                    <React.Fragment key={`${mode.id}-${map.map}`}>
                      <tr
                        className={isExpanded ? 'is-expanded' : ''}
                        onClick={() => setExpandedMap(isExpanded ? '' : map.map)}
                      >
                        <td>
                          <strong>{map.map}</strong>
                          <div className="match-room-map-badges">
                            {map.our.mostPlayed && <span>Our Most Played</span>}
                            {map.opponent.mostPlayed && <span>Opponent Most Played</span>}
                          </div>
                        </td>
                        <td>{map.our.played ? formatRecord(map.our) : 'No data'}</td>
                        <td>{map.our.played ? formatPercent(map.our.winRate) : '-'}</td>
                        <td>{map.our.played ? formatPercent(map.our.usage) : '-'}</td>
                        <td>{map.opponent.played ? formatRecord(map.opponent) : 'No data'}</td>
                        <td>{map.opponent.played ? formatPercent(map.opponent.winRate) : '-'}</td>
                        <td>{map.opponent.played ? formatPercent(map.opponent.usage) : '-'}</td>
                        <td className={getDeltaClass(map.delta)}>{hasAnyData ? formatDelta(map.delta) : 'No data'}</td>
                        <td>{map.confidence.label}</td>
                        <td>{map.matchupLabel}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="match-room-expanded-row">
                          <td colSpan="10">
                            <div className="match-room-recent-map-grid">
                              <div>
                                <span>{teamName || 'Our Team'} recent on {map.map}</span>
                                <ResultSequence form={map.our.recent} />
                              </div>
                              <div>
                                <span>{opponentName || 'Opponent'} recent on {map.map}</span>
                                <ResultSequence form={map.opponent.recent} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

const OpponentList = ({ title, items, side = 'opponent', empty = 'No recorded data.' }) => (
  <section className="match-room-panel">
    <div className="match-room-panel-head">
      <span>{title}</span>
    </div>
    {items.length ? (
      <div className="match-room-opponent-list">
        {items.map((item) => (
          <div className="match-room-opponent-list-row" key={`${title}-${item.mode}-${item.map}`}>
            <strong>{item.map} {item.mode}</strong>
            <span>{formatRecord(item[side])} / {formatPercent(item[side].winRate)}</span>
            <small>{item[side].played} maps / {item[side].confidence.label} confidence</small>
          </div>
        ))}
      </div>
    ) : <p className="match-room-muted">{empty}</p>}
  </section>
);

const OpponentTab = ({
  analytics,
  analyticsLoading,
  analyticsError,
  datasetFilter,
  setDatasetFilter,
  opponentName,
}) => {
  const opponent = analytics.opponent;

  return (
    <div className="match-room-opponent-tab">
      <section className="match-room-panel match-room-opponent-hero">
        <div className="match-room-panel-head match-room-analytics-head">
          <div>
            <span>Opponent Scouting</span>
            <h3>{opponentName || 'Opponent'}</h3>
            <p>Recorded results only. Dataset: {DATASET_FILTERS.find((item) => item.value === datasetFilter)?.label || 'All'}.</p>
          </div>
          <DatasetFilter value={datasetFilter} onChange={setDatasetFilter} />
        </div>
        <AnalyticsState loading={analyticsLoading} error={analyticsError} opponentName={opponentName} />
        <div className="match-room-opponent-summary">
          <StatRecord label="Overall Record" record={opponent.overall} recent={opponent.recent} />
          <div className="match-room-stat-record">
            <span>Strongest Recorded Mode</span>
            <strong>{opponent.strongestMode?.label || 'No data'}</strong>
            <small>
              {opponent.strongestMode
                ? `${formatRecord(opponent.strongestMode.opponent)} / ${formatPercent(opponent.strongestMode.opponent.winRate)} WR`
                : 'No recorded mode sample'}
            </small>
          </div>
          <div className="match-room-stat-record">
            <span>Weakest Recorded Mode</span>
            <strong>{opponent.weakestMode?.label || 'No reliable data'}</strong>
            <small>
              {opponent.weakestMode
                ? `${formatRecord(opponent.weakestMode.opponent)} / ${formatPercent(opponent.weakestMode.opponent.winRate)} WR`
                : 'Needs at least 3 recorded maps'}
            </small>
          </div>
        </div>
      </section>

      <section className="match-room-panel">
        <div className="match-room-panel-head">
          <span>Recent Form</span>
          <p>{opponent.recent.label}</p>
        </div>
        <ResultSequence form={opponent.recent} />
        <div className="match-room-recent-mode-grid">
          {opponent.modes.map((mode) => (
            <div key={`recent-${mode.id}`}>
              <strong>{mode.label}</strong>
              <span>{mode.opponentRecent.label}</span>
            </div>
          ))}
        </div>
      </section>

      <OpponentList title="Strongest Recorded Maps" items={opponent.strongestMaps} />
      <OpponentList title="Most Played Maps" items={opponent.mostPlayedMaps} />
      <OpponentList title="Lowest Performing Recorded Maps" items={opponent.weakestMaps} />
      <OpponentList title="Limited Data Maps" items={opponent.limitedDataMaps} />

      <section className="match-room-panel match-room-opponent-insights">
        <div className="match-room-panel-head">
          <span>Recorded Tendency Insights</span>
          <p>Deterministic notes based on recorded results only.</p>
        </div>
        {opponent.insights.length ? (
          <ul>
            {opponent.insights.map((insight) => <li key={insight}>{insight}</li>)}
          </ul>
        ) : (
          <p className="match-room-muted">No opponent scouting insights are available for the selected dataset yet.</p>
        )}
      </section>
    </div>
  );
};

const PlaceholderTab = ({ title, body, actionLabel }) => (
  <EmptyPanel
    title={title}
    body={body}
    action={actionLabel ? <button type="button" className="match-room-secondary-btn" disabled>{actionLabel}</button> : null}
  />
);

const MatchRoom = ({ teamName, teamId, uid }) => {
  const {
    matchRooms,
    roomsLoading,
    roomsError,
    seasonId,
    seasonYear,
    seasonLabel,
    seasonLoading,
    seasonError,
    configLoading,
    configError,
    opponentOptions,
    competitionOptions,
    statusOptions,
    createMatchRoom,
    updateMatchRoom,
    deleteMatchRoom,
    updatePreparationTasks,
    updateWinConditions,
    updateNotes,
  } = useMatchRooms({ teamId, uid });

  const [selectedMatchRoomId, setSelectedMatchRoomId] = useState(null);
  const [activeMatchRoomTab, setActiveMatchRoomTab] = useState('Overview');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [pendingOpenRoomId, setPendingOpenRoomId] = useState(null);
  const [datasetFilter, setDatasetFilter] = useState('ALL');

  const selectedRoom = useMemo(
    () => matchRooms.find((room) => room.id === selectedMatchRoomId) || null,
    [matchRooms, selectedMatchRoomId]
  );

  const {
    analytics,
    analyticsLoading,
    analyticsError,
  } = useMatchRoomAnalytics({
    teamId,
    seasonId,
    seasonYear,
    selectedRoom,
    opponentOptions,
    datasetFilter,
  });

  useEffect(() => {
    if (selectedMatchRoomId && !selectedRoom && !roomsLoading) {
      setSelectedMatchRoomId(null);
      setActiveMatchRoomTab('Overview');
    }
  }, [roomsLoading, selectedMatchRoomId, selectedRoom]);

  useEffect(() => {
    if (!pendingOpenRoomId) return;
    if (matchRooms.some((room) => room.id === pendingOpenRoomId)) {
      setSelectedMatchRoomId(pendingOpenRoomId);
      setActiveMatchRoomTab('Overview');
      setPendingOpenRoomId(null);
    }
  }, [matchRooms, pendingOpenRoomId]);

  useEffect(() => {
    setSelectedMatchRoomId(null);
    setActiveMatchRoomTab('Overview');
    setPendingOpenRoomId(null);
    setDatasetFilter('ALL');
  }, [seasonId]);

  const handleCreateRoom = async (form) => {
    const roomId = await createMatchRoom(form);
    setPendingOpenRoomId(roomId);
    setIsCreateModalOpen(false);
  };

  const handleEditRoom = async (form) => {
    if (!editingRoom) return;
    await updateMatchRoom(editingRoom.id, form);
    setEditingRoom(null);
  };

  const handleDeleteRoom = async () => {
    if (!selectedRoom) return;
    const confirmed = window.confirm('Delete this Match Room? Preparation tasks, win conditions, and notes stored inside it will also be removed. This cannot be undone.');
    if (!confirmed) return;

    try {
      await deleteMatchRoom(selectedRoom.id);
      setSelectedMatchRoomId(null);
      setActiveMatchRoomTab('Overview');
    } catch (error) {
      console.error('Failed to delete Match Room', error);
      toast.error('Failed to delete Match Room.');
    }
  };

  const safeTaskUpdate = async (nextTasks) => {
    if (!selectedRoom) return;
    try {
      await updatePreparationTasks(selectedRoom.id, nextTasks);
    } catch (error) {
      console.error('Failed to update preparation tasks', error);
      toast.error('Failed to update preparation task.');
    }
  };

  const safeWinConditionUpdate = async (nextConditions) => {
    if (!selectedRoom) return;
    try {
      await updateWinConditions(selectedRoom.id, nextConditions);
    } catch (error) {
      console.error('Failed to update win conditions', error);
      toast.error('Failed to update win conditions.');
    }
  };

  const seasonUnavailable = !seasonLoading && (!seasonId || seasonError);
  const createDisabled = seasonUnavailable || configLoading || !opponentOptions.length || !competitionOptions.length;

  if (selectedRoom) {
    const progress = getProgress(selectedRoom.preparationTasks);

    return (
      <div className="match-room-page">
        <div className="match-room-detail-top">
          <button type="button" className="match-room-back-btn" onClick={() => setSelectedMatchRoomId(null)}>
            Back to Match Rooms
          </button>
          <div className="match-room-detail-actions">
            <span className={`match-room-status status-${selectedRoom.status}`}>{getStatusLabel(selectedRoom.status)}</span>
            <button type="button" className="match-room-secondary-btn" onClick={() => setEditingRoom(selectedRoom)}>Edit</button>
            <button type="button" className="match-room-danger-btn" onClick={handleDeleteRoom}>Delete Match Room</button>
          </div>
        </div>

        <header className="match-room-detail-header">
          <div>
            <h1>{teamName || 'Team'} vs {String(selectedRoom.opponentName || '').toUpperCase()}</h1>
            <p>{selectedRoom.competitionName} / {formatDate(selectedRoom.matchDate)} / {selectedRoom.format}</p>
          </div>
          <div className="match-room-detail-progress">
            <strong>{progress.percent}%</strong>
            <span>{progress.complete} / {progress.total} tasks</span>
          </div>
        </header>

        <nav className="match-room-tabs" aria-label="Match Room tabs">
          {MATCH_ROOM_TABS.map((tab) => (
            <button
              type="button"
              key={tab}
              className={activeMatchRoomTab === tab ? 'active' : ''}
              onClick={() => setActiveMatchRoomTab(tab)}
            >
              {tab}
            </button>
          ))}
        </nav>

        {activeMatchRoomTab === 'Overview' && (
          <OverviewTab
            room={selectedRoom}
            analytics={analytics}
            analyticsLoading={analyticsLoading}
            analyticsError={analyticsError}
            datasetFilter={datasetFilter}
            setDatasetFilter={setDatasetFilter}
            teamName={teamName}
            opponentName={selectedRoom.opponentName}
            onAddWinCondition={(value) => safeWinConditionUpdate([
              ...selectedRoom.winConditions,
              { id: makeLocalId('condition'), text: value, createdAt: new Date().toISOString() },
            ])}
            onRemoveWinCondition={(conditionId) => safeWinConditionUpdate(
              selectedRoom.winConditions.filter((item) => {
                const condition = typeof item === 'string' ? { id: item, text: item } : item;
                return condition.id !== conditionId && condition.text !== conditionId;
              })
            )}
          />
        )}
        {activeMatchRoomTab === 'Map Pool' && (
          <MapPoolTab
            analytics={analytics}
            analyticsLoading={analyticsLoading}
            analyticsError={analyticsError}
            datasetFilter={datasetFilter}
            setDatasetFilter={setDatasetFilter}
            teamName={teamName}
            opponentName={selectedRoom.opponentName}
          />
        )}
        {activeMatchRoomTab === 'Opponent' && (
          <OpponentTab
            analytics={analytics}
            analyticsLoading={analyticsLoading}
            analyticsError={analyticsError}
            datasetFilter={datasetFilter}
            setDatasetFilter={setDatasetFilter}
            opponentName={selectedRoom.opponentName}
          />
        )}
        {activeMatchRoomTab === 'Players' && (
          <PlaceholderTab title="Players" body="Player matchup data and opponent threats will appear here." />
        )}
        {activeMatchRoomTab === 'Preparation' && (
          <PreparationTab
            room={selectedRoom}
            onToggleTask={(taskId) => safeTaskUpdate(selectedRoom.preparationTasks.map((task) => (
              task.id === taskId
                ? {
                    ...task,
                    completed: !task.completed,
                    completedAt: task.completed ? null : new Date().toISOString(),
                  }
                : task
            )))}
            onAddTask={(title) => safeTaskUpdate([
              ...selectedRoom.preparationTasks,
              {
                id: makeLocalId('task'),
                title,
                completed: false,
                createdAt: new Date().toISOString(),
                completedAt: null,
              },
            ])}
            onDeleteTask={(taskId) => safeTaskUpdate(selectedRoom.preparationTasks.filter((task) => task.id !== taskId))}
          />
        )}
        {activeMatchRoomTab === 'Strategies' && (
          <PlaceholderTab
            title="Strategies"
            body="Strategies from My Playbook will eventually be linked to this Match Room."
            actionLabel="Link Strategy"
          />
        )}
        {activeMatchRoomTab === 'Notes' && (
          <NotesTab
            room={selectedRoom}
            onSaveNotes={(notes) => updateNotes(selectedRoom.id, notes)}
          />
        )}

        {editingRoom && (
          <MatchRoomFormModal
            title="Edit Match Room"
            submitLabel="Save Changes"
            initialRoom={editingRoom}
            opponentOptions={opponentOptions}
            competitionOptions={competitionOptions}
            statusOptions={statusOptions}
            configLoading={configLoading}
            configError={configError}
            onClose={() => setEditingRoom(null)}
            onSubmit={handleEditRoom}
          />
        )}
      </div>
    );
  }

  return (
    <div className="match-room-page">
      <header className="match-room-header">
        <div>
          <span className="match-room-eyebrow">Preparation</span>
          <h1>Match Room</h1>
          <p>Prepare for upcoming opponents using your team's stats, scouting data, strategies, and match plans.</p>
          <small>{seasonLabel || 'Season unavailable'}</small>
        </div>
        <button
          type="button"
          className="match-room-primary-btn"
          onClick={() => setIsCreateModalOpen(true)}
          disabled={createDisabled}
        >
          + Create Match Room
        </button>
      </header>

      {seasonLoading && <EmptyPanel title="Loading Match Rooms" body="Resolving the active season for this team." />}
      {seasonUnavailable && (
        <EmptyPanel
          title="Season unavailable"
          body={seasonError || 'Select or configure a season before creating a Match Room.'}
        />
      )}
      {!seasonUnavailable && roomsError && <EmptyPanel title="Unable to load Match Rooms" body={roomsError} />}
      {!seasonUnavailable && !roomsError && roomsLoading && (
        <EmptyPanel title="Loading Match Rooms" body="Loading Match Rooms for the selected season." />
      )}
      {!seasonUnavailable && !roomsError && !roomsLoading && matchRooms.length === 0 && (
        <div className="match-room-empty-state">
          <EmptyPanel
            title="Prepare for your next opponent"
            body="Create a Match Room to bring opponent scouting, map performance, preparation tasks, and linked strategies into one workspace."
            action={
              <button
                type="button"
                className="match-room-primary-btn"
                onClick={() => setIsCreateModalOpen(true)}
                disabled={createDisabled}
              >
                Create Match Room
              </button>
            }
          />
          <p className="match-room-muted">
            {configLoading
              ? 'Loading configured opponents and competitions...'
              : !opponentOptions.length
                ? 'No opponents configured yet.'
                : !competitionOptions.length
                  ? 'No competitions configured yet.'
                  : 'Match Rooms are scoped to the selected season.'}
          </p>
        </div>
      )}
      {!seasonUnavailable && !roomsError && !roomsLoading && matchRooms.length > 0 && (
        <section className="match-room-list-grid" aria-label="Match Rooms">
          {matchRooms.map((room) => (
            <MatchRoomCard key={room.id} room={room} teamName={teamName} onOpen={setSelectedMatchRoomId} />
          ))}
        </section>
      )}

      {isCreateModalOpen && (
        <MatchRoomFormModal
          title="Create Match Room"
          submitLabel="Create Match Room"
          opponentOptions={opponentOptions}
          competitionOptions={competitionOptions}
          statusOptions={statusOptions}
          configLoading={configLoading}
          configError={configError}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateRoom}
        />
      )}
    </div>
  );
};

export default MatchRoom;
